import Api from 'Api';
import { LoginResponseShape, UserAccountShape } from 'models/auth';
import { useEffect, useState } from 'react';
import { useGetProfileQuery, useLoginMutation } from 'store/_Api/Auth';
import useBaseLinks from './Links/useBaseLinks';
import { skipToken } from '@reduxjs/toolkit/query';

type Return = {
  isLoggedIn: boolean;
  logout: () => Promise<void>;
  profile: UserAccountShape | undefined;
  isCustomer: boolean;
  login: (data: FormData) => Promise<LoginResponseShape>;
  isLoading;
};

const useAuth = (): Return => {
  const [isLoggedIn, setIsLoggedIn] = useState(
    Boolean(localStorage.getItem('userAuthToken')),
  );
  const { getLink, refetch } = useBaseLinks();
  const [accountUrl, setAccountUrl] = useState('');
  const [login] = useLoginMutation();

  useEffect(() => {
    const url = getLink('me');
    if (url) {
      setAccountUrl(url);
    }
  }, [getLink]);

  const { data: profile, isLoading } = useGetProfileQuery(
    accountUrl ?? skipToken,
  );

  useEffect(() => {
    if (profile) {
      localStorage.setItem('loggedInUserUUID', profile.user_uuid);
    }
  }, [profile]);

  const handleLogout = async () => {
    await Api.post('logout');
    localStorage.removeItem('userAuthToken');
    localStorage.removeItem('loggedInUserUUID');
    setIsLoggedIn(false);
  };

  const handleLogin = async (data: FormData): Promise<LoginResponseShape> => {
    return new Promise((resolve, reject) => {
      const endpoint = getLink('login');
      if (!endpoint) {
        console.error('Could not get endpoint when trying to run login');
        reject('Not being able to find the login link');
        return;
      }

      try {
        const response = login({
          url: endpoint,
          userData: data,
        }).unwrap();
        refetch();
        resolve(response);
      } catch (error) {
        console.error('Cought error when trying to login ->', error);
        reject(error);
      }
    });
  };

  return {
    isLoggedIn,
    profile,
    isCustomer: profile?.role === 'customer',
    logout: handleLogout,
    login: handleLogin,
    isLoading,
  };
};

export default useAuth;
